/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/** @jsx jsx */
import React, { forwardRef } from 'react';
import { css, jsx } from '@emotion/react';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { N0, N500, N700, R400, Y300 } from '@atlaskit/theme/colors';
const CSS_VAR_TEXT_COLOR = '--banner-text-color';
const textStyles = css({
  color: `var(${CSS_VAR_TEXT_COLOR})`,
  fontWeight: "var(--ds-font-weight-medium, 500)",
  lineHeight: "var(--ds-font-lineHeight-300, 24px)",
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  // eslint-disable-next-line @atlaskit/design-system/no-nested-styles
  'a, a:visited, a:hover, a:focus, a:active': {
    color: 'currentColor',
    textDecoration: 'underline'
  }
});
const backgroundColors = {
  warning: 'color.background.warning.bold',
  error: 'color.background.danger.bold',
  announcement: 'color.background.neutral.bold'
};
const tokenBackgroundColors = {
  warning: `var(--ds-background-warning-bold, ${Y300})`,
  error: `var(--ds-background-danger-bold, ${R400})`,
  announcement: `var(--ds-background-neutral-bold, ${N500})`
};
const tokenTextColors = {
  warning: `var(--ds-text-warning-inverse, ${N700})`,
  error: `var(--ds-text-inverse, ${N0})`,
  announcement: `var(--ds-text-inverse, ${N0})`
};
const containerStyles = xcss({
  overflow: 'hidden',
  maxHeight: 'size.500'
});
const iconWrapperStyles = xcss({
  display: 'block',
  width: 'size.200',
  height: 'size.200',
  // This matches Icon's "medium" size, without this the (line-)height is greater than that of the Icon
  flexShrink: '0'
});
/**
 * __Banner__
 *
 * A banner displays a prominent message at the top of the screen.
 *
 * - [Examples](https://atlassian.design/components/banner/examples)
 * - [Code](https://atlassian.design/components/banner/code)
 * - [Usage](https://atlassian.design/components/banner/usage)
 */
const Banner = /*#__PURE__*/forwardRef(({
  appearance = 'warning',
  children,
  icon,
  testId
}, ref) => {
  const appearanceType = appearance in backgroundColors ? appearance : 'warning';
  return jsx(Box, {
    xcss: containerStyles,
    backgroundColor: backgroundColors[appearanceType],
    padding: "space.150",
    testId: testId,
    ref: ref,
    role: "alert"
  }, jsx(Inline, {
    space: "space.050",
    alignBlock: "center",
    alignInline: "start"
  }, icon ? jsx(Box, {
    as: "span",
    xcss: iconWrapperStyles,
    style: {
      fill: tokenBackgroundColors[appearanceType],
      color: tokenTextColors[appearanceType]
    }
  }, icon) : null, jsx("span", {
    style: {
      [CSS_VAR_TEXT_COLOR]: tokenTextColors[appearanceType]
    },
    css: textStyles
  }, children)));
});
export default Banner;